import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { LineChart, Line, ComposedChart, Bar, Legend, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Droplets, Milk, Beaker, TrendingUp, AlertTriangle, Users, Factory, Bell, MessageCircle, ArrowRight, Clock, Wallet, ArrowDownRight, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const ROLE_LABEL = {
  dueno: "Dueño",
  admin: "Administración",
  laboratorio: "Laboratorio",
  produccion: "Producción",
  logistica: "Logística",
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Buenos días";
  if (h < 19) return "Buenas tardes";
  return "Buenas noches";
};

const KPI = ({ icon: Icon, label, value, unit, tone = "default", testid }) => (
  <Card data-testid={testid} className="p-5 border-stone-200 bg-white shadow-sm">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500">{label}</div>
        <div className="mt-3 font-outfit font-semibold text-3xl text-stone-900 tabular-nums">
          {value}
          {unit && <span className="text-base font-medium text-stone-500 ml-1">{unit}</span>}
        </div>
      </div>
      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${tone === "primary" ? "bg-[#1E3A8A]/10 text-[#1E3A8A]" : tone === "alert" ? "bg-amber-50 text-amber-700" : "bg-stone-100 text-stone-600"}`}>
        <Icon className="w-4.5 h-4.5" strokeWidth={1.75} />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [notifSum, setNotifSum] = useState({ pending_count: 0, critical_count: 0, warning_count: 0, upcoming_week: [], total_pending_amount: 0, critical_amount: 0 });
  const [topAlerts, setTopAlerts] = useState([]);
  const [cashflow, setCashflow] = useState(null);
  const canSeeCashflow = user?.role === "dueno" || !!user?.permissions?.tesoreria?.view || !!user?.permissions?.cobranza?.view || !!user?.permissions?.cuentas_por_pagar?.view;

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data));
    const loadNotif = async () => {
      try {
        const [s, list] = await Promise.all([api.get("/notifications/summary"), api.get("/notifications?status=pending&limit=5")]);
        setNotifSum(s.data);
        setTopAlerts(list.data);
      } catch { /* silent */ }
    };
    loadNotif();
    if (canSeeCashflow) {
      api.get("/dashboard/cashflow-projection").then((r) => setCashflow(r.data)).catch(() => {});
    }
    // eslint-disable-next-line
  }, []);

  if (!data) return <div className="p-8 text-stone-500">Cargando…</div>;

  const firstName = (user?.name || "").split(" ")[0];

  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-outfit font-semibold uppercase tracking-[0.15em] text-stone-500">{greeting()}</p>
          <h1 className="font-outfit font-bold text-3xl lg:text-4xl tracking-tight text-stone-900 mt-1" data-testid="dashboard-greeting">
            Hola, <span className="text-[#1E3A8A]">{firstName || "usuario"}</span>
          </h1>
          <p className="text-sm text-stone-500 mt-1">Panel de {ROLE_LABEL[user?.role] || user?.role} · Estado operativo y financiero en tiempo real.</p>
        </div>
        <div className="text-right">
          <p className="text-xs font-outfit font-semibold uppercase tracking-wider text-stone-500">{new Date().toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testid="kpi-liters-today" icon={Droplets} label="Litros recibidos hoy" value={data.liters_today.toLocaleString("es-MX")} unit="L" tone="primary" />
        <KPI testid="kpi-cream-today" icon={Milk} label="Crema producida hoy" value={data.cream_today_kg.toLocaleString("es-MX")} unit="kg" />
        <KPI testid="kpi-curd-today" icon={Factory} label="Cuajada producida hoy" value={data.curd_today_kg.toLocaleString("es-MX")} unit="kg" />
        <KPI testid="kpi-correction-avg" icon={TrendingUp} label="Corrección promedio" value={`$${data.correction_cost_avg.toLocaleString("es-MX")}`} />
      </div>

      {/* Cobranza Alerts Widget */}
      {(notifSum.pending_count > 0 || notifSum.upcoming_week?.length > 0) && (
        <CobranzaAlertsWidget notifSum={notifSum} topAlerts={topAlerts} />
      )}

      {/* Flujo de Caja Proyectado */}
      {canSeeCashflow && cashflow && <CashflowWidget cashflow={cashflow} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 border-stone-200 bg-white shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit font-semibold text-lg text-stone-900">Litros recibidos · últimos 7 días</h3>
            <span className="text-xs text-stone-500 font-ibm-plex">Total acumulado: {data.total_liters.toLocaleString("es-MX")} L</span>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.liters_series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                <XAxis dataKey="date" stroke="#78716c" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#78716c" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #e7e5e4", fontFamily: "IBM Plex Sans" }} />
                <Line type="monotone" dataKey="liters" stroke="#1E3A8A" strokeWidth={2.5} dot={{ r: 4, fill: "#1E3A8A" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-stone-200 bg-white shadow-sm">
          <h3 className="font-outfit font-semibold text-lg text-stone-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" strokeWidth={1.75} /> Alertas
          </h3>
          {data.alerts.length === 0 ? (
            <p className="text-sm text-stone-500">Sin alertas activas.</p>
          ) : (
            <ul className="space-y-2" data-testid="dashboard-alerts">
              {data.alerts.map((a, i) => (
                <li key={i} className="text-sm border-l-2 border-amber-500 pl-3 py-1 text-stone-700 bg-amber-50/40">{a.msg}</li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 border-stone-200 bg-white shadow-sm">
          <h3 className="font-outfit font-semibold text-lg text-stone-900 mb-4">Recepciones recientes</h3>
          {data.recent_receptions.length === 0 ? (
            <p className="text-sm text-stone-500">Aún no hay recepciones.</p>
          ) : (
            <div className="divide-y divide-stone-100">
              {data.recent_receptions.map((r) => (
                <div key={r.id} className="flex items-center justify-between py-3">
                  <div>
                    <div className="font-ibm-plex font-medium text-sm text-stone-900">{r.folio}</div>
                    <div className="text-xs text-stone-500">{r.supplier_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-ibm-plex font-semibold tabular-nums text-stone-900">{r.liters?.toLocaleString("es-MX")} L</div>
                    <div className="text-xs text-stone-500">{new Date(r.created_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6 border-stone-200 bg-white shadow-sm">
          <h3 className="font-outfit font-semibold text-lg text-stone-900 mb-4">Resumen general</h3>
          <div className="grid grid-cols-2 gap-4">
            <Stat label="Proveedores" value={data.suppliers_count} icon={Users} />
            <Stat label="Recepciones" value={data.receptions_count} icon={Droplets} />
            <Stat label="Lotes producidos" value={data.productions_count} icon={Factory} />
            <Stat label="Muestras de lab" value={data.lab_samples_count} icon={Beaker} />
          </div>
        </Card>
      </div>
    </div>
  );
}

const Stat = ({ label, value, icon: Icon }) => (
  <div className="border border-stone-200 rounded-md p-3">
    <div className="flex items-center gap-2 text-stone-500">
      <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
      <span className="text-xs font-outfit uppercase tracking-wider">{label}</span>
    </div>
    <div className="mt-1.5 font-outfit font-semibold text-xl text-stone-900 tabular-nums">{value}</div>
  </div>
);


const money = (n) => `$${(n ?? 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CobranzaAlertsWidget = ({ notifSum, topAlerts }) => {
  const hasCritical = notifSum.critical_count > 0;
  const openWa = async (n) => {
    if (!n.wa_url) return toast.error("Cliente sin teléfono");
    try { await api.post(`/notifications/${n.id}/mark-sent`); } catch { /* noop */ }
    window.open(n.wa_url, "_blank");
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="cobranza-alerts-widget">
      <Card className={`lg:col-span-2 p-5 border shadow-sm relative overflow-hidden ${hasCritical ? "border-red-200 bg-gradient-to-br from-red-50 to-white dark:from-red-950/30 dark:to-stone-950 dark:border-red-900" : "border-amber-200 bg-gradient-to-br from-amber-50 to-white dark:from-amber-950/20 dark:to-stone-950 dark:border-amber-900"}`}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-9 h-9 rounded-md flex items-center justify-center ${hasCritical ? "bg-red-500 text-white" : "bg-amber-500 text-white"}`}>
              <Bell className="w-4 h-4" strokeWidth={2} />
            </div>
            <div>
              <div className="font-outfit font-semibold text-sm text-stone-900 dark:text-stone-100">Alertas de cobranza</div>
              <div className="text-xs text-stone-500 dark:text-stone-400">
                {notifSum.pending_count} pendientes · {notifSum.critical_count > 0 && <span className="text-red-700 dark:text-red-400 font-semibold">{notifSum.critical_count} críticas · </span>}
                total en riesgo <b className="text-stone-800 dark:text-stone-200">{money(notifSum.total_pending_amount)}</b>
              </div>
            </div>
          </div>
          <Link to="/cobranza" className="text-xs text-[#1E3A8A] dark:text-blue-400 hover:underline flex items-center gap-1 font-outfit font-medium">
            Ir a Cobranza <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        {topAlerts.length === 0 ? (
          <div className="text-sm text-stone-500 py-3">Sin alertas pendientes 🎉</div>
        ) : (
          <div className="space-y-2">
            {topAlerts.slice(0, 4).map((n) => (
              <div key={n.id} className="flex items-center gap-2 py-2 border-t border-stone-100 dark:border-stone-800 first:border-0">
                <span className={`w-1.5 h-8 rounded ${n.severity === "critical" ? "bg-red-500" : n.severity === "warning" ? "bg-amber-500" : "bg-blue-500"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-stone-900 dark:text-stone-100 font-medium truncate">{n.customer_name} <span className="text-[#1E3A8A] dark:text-blue-400 text-xs">· {n.sale_folio}</span></div>
                  <div className="text-xs text-stone-500 dark:text-stone-400">
                    {n.days_relative > 0 ? <span className="text-red-600 dark:text-red-400 font-medium">Vencida {n.days_relative}d</span> : n.days_relative === 0 ? <span className="text-amber-600 dark:text-amber-400 font-medium">Vence hoy</span> : <>En {Math.abs(n.days_relative)}d</>}
                    <span> · <b className="tabular-nums text-stone-800 dark:text-stone-200">{money(n.balance)}</b></span>
                  </div>
                </div>
                {n.wa_url && (
                  <button onClick={() => openWa(n)} title="Enviar por WhatsApp" className="p-1.5 rounded-md text-emerald-600 hover:text-white hover:bg-emerald-600 transition-colors">
                    <MessageCircle className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card className="p-5 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-md bg-[#1E3A8A]/10 text-[#1E3A8A] dark:text-blue-400 flex items-center justify-center">
            <Clock className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <div>
            <div className="font-outfit font-semibold text-sm text-stone-900 dark:text-stone-100">Por vencer esta semana</div>
            <div className="text-xs text-stone-500 dark:text-stone-400">{notifSum.upcoming_week?.length || 0} facturas · próximos 7 días</div>
          </div>
        </div>
        {(notifSum.upcoming_week?.length || 0) === 0 ? (
          <div className="text-sm text-stone-500 py-3">Sin vencimientos próximos.</div>
        ) : (
          <div className="space-y-2">
            {notifSum.upcoming_week.slice(0, 4).map((u) => (
              <div key={u.sale_id} className="flex items-center justify-between py-1.5 border-t border-stone-100 dark:border-stone-800 first:border-0">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-stone-800 dark:text-stone-200 truncate">{u.customer_name}</div>
                  <div className="text-[10px] text-stone-500 dark:text-stone-400">{u.folio} · en {u.days_until_due}d</div>
                </div>
                <div className="text-xs tabular-nums font-semibold text-stone-700 dark:text-stone-300">{money(u.balance)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

const CashflowWidget = ({ cashflow }) => {
  const net = cashflow.net_semana ?? 0;
  const hasOverdue = (cashflow.overdue_cobros || 0) > 0 || (cashflow.overdue_pagos || 0) > 0;
  const chartData = (cashflow.series || []).map((d) => ({
    ...d,
    label: new Date(`${d.date}T12:00:00`).toLocaleDateString("es-MX", { weekday: "short", day: "numeric" }),
  }));
  return (
    <Card className="p-6 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm" data-testid="cashflow-widget">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-[#1E3A8A]/10 text-[#1E3A8A] dark:text-blue-400 flex items-center justify-center">
            <Wallet className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <div>
            <div className="font-outfit font-semibold text-base text-stone-900 dark:text-stone-100">Flujo de caja proyectado</div>
            <div className="text-xs text-stone-500 dark:text-stone-400">Cobros esperados vs pagos por hacer · próximos 7 días</div>
          </div>
        </div>
        <Link to="/tesoreria" className="text-xs text-[#1E3A8A] dark:text-blue-400 hover:underline flex items-center gap-1 font-outfit font-medium">
          Ir a Tesorería <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="p-3 rounded-lg border border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-900/40">
          <div className="text-[10px] font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500">Saldo actual</div>
          <div className="mt-1 font-outfit font-semibold text-lg text-stone-900 dark:text-stone-100 tabular-nums" data-testid="cashflow-starting-balance">{money(cashflow.starting_balance)}</div>
        </div>
        <div className="p-3 rounded-lg border border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-950/30">
          <div className="text-[10px] font-outfit font-semibold uppercase tracking-[0.1em] text-emerald-700 dark:text-emerald-400 flex items-center gap-1"><ArrowUpRight className="w-3 h-3" /> Cobros esperados</div>
          <div className="mt-1 font-outfit font-semibold text-lg text-emerald-700 dark:text-emerald-400 tabular-nums">{money(cashflow.total_cobros_semana)}</div>
        </div>
        <div className="p-3 rounded-lg border border-red-200 dark:border-red-900 bg-red-50/60 dark:bg-red-950/30">
          <div className="text-[10px] font-outfit font-semibold uppercase tracking-[0.1em] text-red-700 dark:text-red-400 flex items-center gap-1"><ArrowDownRight className="w-3 h-3" /> Pagos por hacer</div>
          <div className="mt-1 font-outfit font-semibold text-lg text-red-700 dark:text-red-400 tabular-nums">{money(cashflow.total_pagos_semana)}</div>
        </div>
        <div className={`p-3 rounded-lg border ${net >= 0 ? "border-[#1E3A8A]/20 bg-[#1E3A8A]/5" : "border-amber-200 dark:border-amber-900 bg-amber-50/60 dark:bg-amber-950/30"}`}>
          <div className={`text-[10px] font-outfit font-semibold uppercase tracking-[0.1em] ${net >= 0 ? "text-[#1E3A8A] dark:text-blue-400" : "text-amber-700 dark:text-amber-400"}`}>Saldo proyectado (7d)</div>
          <div className={`mt-1 font-outfit font-semibold text-lg tabular-nums ${net >= 0 ? "text-[#1E3A8A] dark:text-blue-400" : "text-amber-700 dark:text-amber-400"}`} data-testid="cashflow-ending-balance">{money(cashflow.ending_balance)}</div>
        </div>
      </div>

      {hasOverdue && (
        <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-md px-3 py-2 mb-4" data-testid="cashflow-overdue-banner">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          <span>
            Ya vencido sin cobrar: <b>{money(cashflow.overdue_cobros)}</b> · Ya vencido sin pagar: <b>{money(cashflow.overdue_pagos)}</b> (no incluido en la proyección de 7 días)
          </span>
        </div>
      )}

      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
            <XAxis dataKey="label" stroke="#78716c" fontSize={12} tickLine={false} axisLine={false} />
            <YAxis stroke="#78716c" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v / 1000).toLocaleString("es-MX")}k`} />
            <Tooltip
              contentStyle={{ borderRadius: 6, border: "1px solid #e7e5e4", fontFamily: "IBM Plex Sans", fontSize: 12 }}
              formatter={(value, name) => [money(value), name]}
            />
            <Legend wrapperStyle={{ fontSize: 12, fontFamily: "Outfit" }} />
            <Bar dataKey="cobros_esperados" name="Cobros esperados" fill="#10b981" radius={[3, 3, 0, 0]} />
            <Bar dataKey="pagos_esperados" name="Pagos por hacer" fill="#ef4444" radius={[3, 3, 0, 0]} />
            <Line type="monotone" dataKey="saldo_proyectado" name="Saldo proyectado" stroke="#1E3A8A" strokeWidth={2.5} dot={{ r: 3, fill: "#1E3A8A" }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};
